import { REQUEST_STATUS } from '@ubeya/shared/constants';
import { SLOT_WARNING_TYPES } from './constants';

export const LEAVE_STATUS = {
  APPROVED: 'approved',
  PENDING: 'pending'
};

export const LEAVE_SOURCE = {
  PTO: 'pto',
  LEAVE_RECORD: 'leaveRecord',
  LEAVE_REQUEST: 'leaveRequest'
};

// Slot warnings the backend raises when the employee has a leave/day off on the booked date.
export const LEAVE_SLOT_WARNING_TYPES = [
  SLOT_WARNING_TYPES.DAY_OFF.type,
  SLOT_WARNING_TYPES.PTO.type,
  SLOT_WARNING_TYPES.LEAVE_REQUEST.type,
  SLOT_WARNING_TYPES.LEAVE_RECORD.type
];

export const isPendingLeave = (leave) => leave?.status === LEAVE_STATUS.PENDING;

// Legacy timeoffs delete by `timeoffId`. `pto`/`leaveRecord` each delete their own row by `id`. A
// still-pending `leaveRequest` has no row yet, so it cancels by `requestId` instead. Anything
// already processed by payroll is never deletable.
export const getLeaveDeleteState = ({
  isProcessed = false,
  timeoffId = null,
  source = null,
  id = null,
  requestId = null,
  employeeContractId = null,
  status = null
}) => {
  if (isProcessed) {
    return { canDelete: false, deleteId: null, deleteType: null };
  }

  if (timeoffId) {
    return { canDelete: true, deleteId: timeoffId, deleteType: 'timeoff' };
  }

  if (source === LEAVE_SOURCE.LEAVE_REQUEST) {
    return requestId
      ? { canDelete: true, deleteId: requestId, deleteType: source }
      : { canDelete: false, deleteId: null, deleteType: null };
  }

  // pto delete is contract-scoped (`employeeContractId`) - without it there's no valid call to make.
  // A still-pending pto has no `pto` row yet - `id`/`employeeContractId` here are the originating
  // request's, not a real pto's, so this must bail before treating them as a deletable pto id.
  if (source === LEAVE_SOURCE.PTO) {
    return !isPendingLeave({ status }) && id && employeeContractId
      ? { canDelete: true, deleteId: id, deleteType: source }
      : { canDelete: false, deleteId: null, deleteType: null };
  }

  if (source === LEAVE_SOURCE.LEAVE_RECORD && id) {
    return { canDelete: true, deleteId: id, deleteType: source };
  }

  return { canDelete: false, deleteId: null, deleteType: null };
};

// pto / leaveRecord delete through the payroll routes, which require both of these privileges -
// without them the call is rejected, so the delete is disabled up front instead.
const PAYROLL_GATED_DELETE_TYPES = [LEAVE_SOURCE.PTO, LEAVE_SOURCE.LEAVE_RECORD];

export const isDeleteBlockedByPayrollPrivileges = ({ deleteType, hasUnlockPayroll, hasManageHolidayAccrual }) =>
  PAYROLL_GATED_DELETE_TYPES.includes(deleteType) && !(hasUnlockPayroll && hasManageHolidayAccrual);

// figaro's SLOT_WARNING_TYPES has no `slug` - mirrored here from giovanni's
// packages/scheduling/src/types.ts. Keep in sync; each is also a locale key.
export const LEAVE_SLOT_WARNING_SLUGS = {
  [SLOT_WARNING_TYPES.DAY_OFF.type]: 'day_off',
  [SLOT_WARNING_TYPES.PTO.type]: 'pto_in_the_same_day',
  [SLOT_WARNING_TYPES.LEAVE_REQUEST.type]: 'leave_request_for_the_shift',
  [SLOT_WARNING_TYPES.LEAVE_RECORD.type]: 'leave_record_for_the_shift'
};

// The "+" quick-add books a shift that doesn't exist yet, so neither backend warnings engine (both
// shift-scoped) can serve it. Synthesize the same { type, slug, metadata } shape they'd emit, from
// the indications already rendered on the day cell, so EmployeeBookingWarningsRenderer treats it
// identically to a real engine warning.
export const buildLeaveSlotWarnings = ({ timeOffs = [], leaves = [], ptos = [], mappedJobTypes = {} } = {}) => [
  // DayOffInSameDateWarningUseCase: metadata { status, dayOffName }
  ...(timeOffs || [])
    .filter(({ timeoffId, requestId }) => !!timeoffId || !!requestId)
    .map(({ jobTypeId, requestStatus }) => ({
      type: SLOT_WARNING_TYPES.DAY_OFF.type,
      slug: LEAVE_SLOT_WARNING_SLUGS[SLOT_WARNING_TYPES.DAY_OFF.type],
      metadata: {
        status: requestStatus === REQUEST_STATUS.PENDING ? LEAVE_STATUS.PENDING : LEAVE_STATUS.APPROVED,
        dayOffName: mappedJobTypes?.[jobTypeId]?.label ?? null
      }
    })),

  // PtoInSameDateWarningUseCase: metadata { type, status, requestId? } - type is the raw PtoTypeEnum
  // value, resolved at render time. status is what makes a pending pto render "(Pending)".
  ...(ptos || []).map(({ ptoType, status, requestId }) => ({
    type: SLOT_WARNING_TYPES.PTO.type,
    slug: LEAVE_SLOT_WARNING_SLUGS[SLOT_WARNING_TYPES.PTO.type],
    metadata: {
      type: ptoType,
      status: status === LEAVE_STATUS.PENDING ? LEAVE_STATUS.PENDING : LEAVE_STATUS.APPROVED,
      ...(requestId ? { requestId } : {})
    }
  })),

  // LeaveRecordInSameDateWarningUseCase: its own warning type, distinct from PTO - an approved leave
  // record must not read as "Employee has PTO that day" either.
  ...(leaves || [])
    .filter(({ source }) => source === LEAVE_SOURCE.LEAVE_RECORD)
    .map(({ leaveTypeName }) => ({
      type: SLOT_WARNING_TYPES.LEAVE_RECORD.type,
      slug: LEAVE_SLOT_WARNING_SLUGS[SLOT_WARNING_TYPES.LEAVE_RECORD.type],
      metadata: { type: leaveTypeName }
    })),

  // PendingLeaveRequestInSameDateWarningUseCase: its own warning type, distinct from PTO - a pending
  // leave request must not read as "Employee has PTO that day". isPendingLeaveWarning already treats
  // this type as pending on its own, so no `status` field is needed here (matches the backend shape).
  ...(leaves || [])
    .filter(({ source }) => source === LEAVE_SOURCE.LEAVE_REQUEST)
    .map(({ leaveTypeId, leaveTypeName, requestId }) => ({
      type: SLOT_WARNING_TYPES.LEAVE_REQUEST.type,
      slug: LEAVE_SLOT_WARNING_SLUGS[SLOT_WARNING_TYPES.LEAVE_REQUEST.type],
      metadata: { requestId, accountLeaveTypeId: leaveTypeId ?? null, leaveTypeName }
    }))
];

export const getLeaveSlotWarnings = (slotWarnings) =>
  (slotWarnings || []).filter(({ type }) => LEAVE_SLOT_WARNING_TYPES.includes(type));

export const isPendingLeaveWarning = ({ type, metadata } = {}) =>
  metadata?.status === LEAVE_STATUS.PENDING || type === SLOT_WARNING_TYPES.LEAVE_REQUEST.type;

// Each source names its type in a different field: PTO's metadata.type is a raw PtoTypeEnum value
// (resolved via mappedPtoTypes), LEAVE_REQUEST resolves leaveTypeName/accountLeaveTypeId, DAY_OFF
// uses dayOffName (a job type name, not an account_leave_types entry).
const getWarningLeaveTypeName = (metadata, mappedLeaveTypes, mappedPtoTypes) => {
  if (metadata?.leaveTypeName) return metadata.leaveTypeName;
  if (metadata?.dayOffName) return metadata.dayOffName;
  if (mappedLeaveTypes?.[metadata?.accountLeaveTypeId]?.name) {
    return mappedLeaveTypes[metadata.accountLeaveTypeId].name;
  }
  if (typeof metadata?.type !== 'string') return null;

  return mappedPtoTypes?.[metadata.type]?.label || metadata.type;
};

export const getLeaveWarningParts = ({ warning, mappedLeaveTypes = {}, mappedPtoTypes = {} }) => ({
  slug: warning?.slug || null,
  leaveTypeName: getWarningLeaveTypeName(warning?.metadata, mappedLeaveTypes, mappedPtoTypes),
  isPending: isPendingLeaveWarning(warning)
});

// `leaveRequest` deliberately has no entry here - cancelling a pending request (which the employee
// can just raise again) stays a one-click action, unlike deleting a materialized record.
const DELETE_CONFIRMATION_BODIES = {
  timeoff: 'areYouSureYouWantToDeleteTimeOff',
  [LEAVE_SOURCE.PTO]: 'areYouSureYouWantToDeletePto',
  [LEAVE_SOURCE.LEAVE_RECORD]: 'areYouSureYouWantToDeleteLeave'
};

export const getDeleteConfirmationBody = (deleteType) => DELETE_CONFIRMATION_BODIES[deleteType] || null;

// leaveTypeName (leaveRecord/leaveRequest) is already display text, used as-is. ptoType is a raw
// PtoTypeEnum value, resolved via mappedPtoTypes (same lookup payroll/timesheet/PTO logs use).
// 'Custom' has no mappedPtoTypes entry - it's legacy/imported data, not a type an admin can pick -
// so it's special-cased here rather than added to that shared map.
export const getTypeName = (leave, t, mappedPtoTypes = {}) => {
  if (leave?.leaveTypeName) return leave.leaveTypeName;
  if (leave?.ptoType === 'Custom') return t('other');

  return mappedPtoTypes[leave?.ptoType]?.label || t('leave');
};

export const getLeaveSourceLabel = ({ source, t }) => {
  switch (source) {
    case LEAVE_SOURCE.PTO:
      return t('pto');

    case LEAVE_SOURCE.LEAVE_RECORD:
    case LEAVE_SOURCE.LEAVE_REQUEST:
      return t('leave');

    default:
      return t('timeOffRequest');
  }
};

// board chip: "PTO" when approved, "PTO · Pending" when the leave request is still pending
export const formatLeaveChipLabel = ({ leave, t, mappedPtoTypes }) => {
  const typeName = getTypeName(leave, t, mappedPtoTypes);

  return isPendingLeave(leave) ? `${typeName} · ${t('pending')}` : typeName;
};

// booking warning: "Day off - PTO (Pending)"
export const formatLeaveWarningLabel = ({ warning, mappedLeaveTypes = {}, mappedPtoTypes = {}, t }) => {
  const { slug, leaveTypeName, isPending } = getLeaveWarningParts({ warning, mappedLeaveTypes, mappedPtoTypes });

  const label = [slug ? t(slug) : null, leaveTypeName].filter((val) => !!val).join(' - ');

  return `${label}${isPending ? ` (${t('pending')})` : ''}`;
};
